import {useEffect, useState} from 'react';
import {Alert, Button, Form, Input} from '@vector';
import {requestSchoolEnquiry} from './homeService';
import {getRecaptchaToken} from '../portal/recaptcha';
import Layout from './components/Layout';
import brandLayoutProps from './components/brandLayoutProps';
import styles from './QuotePage.module.css';

const FIELD_MAX = 200;
const ENQUIRY_MAX = 4000;

/**
 * "Request a quote" - for brands that sell to schools by quotation only (South Africa).
 *
 * Replaces the Struts page at /home/southafrica/requestAQuote, whose form posted to an action that did not
 * exist, so no enquiry sent from it ever arrived. This one posts to /web/ui/home/public/schoolEnquiry, which
 * emails the brand's sales inbox. Same chrome as the homepage, from the same bundle.
 */
export default function QuotePage({content}) {
    const [form] = Form.useForm();
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        document.title = `${content.quote.heading} - ${content.productName}`;
    }, [content]);

    const submit = async (values) => {
        setBusy(true);
        setResult(null);
        try {
            const recaptchaToken = await getRecaptchaToken('schoolEnquiry');
            const outcome = await requestSchoolEnquiry({...values, recaptchaToken});
            setResult(outcome);
            if (outcome.sent) {
                form.resetFields();
            }
        } catch (e) {
            setResult({sent: false, message: 'We could not send your enquiry. Please try again in a moment.'});
        } finally {
            setBusy(false);
        }
    };

    const required = (label) => [{required: true, whitespace: true, message: `Please enter ${label}.`}];

    return (
        <Layout {...brandLayoutProps(content)}>
            <section className={styles.page} aria-labelledby="quote-heading">
                <div className={styles.intro}>
                    <h1 id="quote-heading" className={styles.heading}>{content.quote.heading}</h1>
                    {content.quote.paragraphs.map((paragraph) => (
                        <p key={paragraph.slice(0, 40)} className={styles.paragraph}>{paragraph}</p>
                    ))}
                    {content.quote.otherEnquiriesEmail && (
                        // Shown as text as well as a link: mailto links do nothing for some visitors.
                        <p className={styles.paragraph}>
                            {content.quote.otherEnquiries}{' '}
                            <a href={`mailto:${content.quote.otherEnquiriesEmail}`}>
                                {content.quote.otherEnquiriesEmail}
                            </a>.
                        </p>
                    )}
                </div>

                <div className={styles.card}>
                    <Form form={form} layout="vertical" requiredMark onFinish={submit} disabled={busy}>
                        <Form.Item label="Contact name" name="contactName" rules={required('your name')}>
                            <Input autoComplete="name" maxLength={FIELD_MAX}/>
                        </Form.Item>
                        <Form.Item label="Contact phone" name="contactPhone" rules={required('a phone number')}>
                            <Input autoComplete="tel" inputMode="tel" maxLength={FIELD_MAX}/>
                        </Form.Item>
                        <Form.Item label="Contact email" name="email"
                                   rules={[...required('your email address'),
                                       {type: 'email', message: 'Please enter a valid email address.'}]}>
                            <Input autoComplete="email" inputMode="email" maxLength={FIELD_MAX}/>
                        </Form.Item>
                        <Form.Item label="School name" name="schoolName" rules={required('the school name')}>
                            <Input autoComplete="organization" maxLength={FIELD_MAX}/>
                        </Form.Item>
                        <Form.Item label="School address" name="schoolAddress"
                                   rules={required('the school address')}>
                            <Input autoComplete="street-address" maxLength={FIELD_MAX}/>
                        </Form.Item>
                        <Form.Item label="Enquiry" name="enquiry" rules={required('your enquiry')}
                                   extra="For example, the number of learners and grades.">
                            <Input.TextArea rows={5} maxLength={ENQUIRY_MAX} showCount/>
                        </Form.Item>

                        <div role="status" aria-live="polite">
                            {result && (
                                <Alert className={styles.alert} type={result.sent ? 'success' : 'error'}
                                       title={result.message} showIcon/>
                            )}
                        </div>

                        <Button surface="accent" size="large" htmlType="submit" loading={busy}>
                            Send enquiry
                        </Button>
                    </Form>
                </div>
            </section>
        </Layout>
    );
}
